import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import MembersTable from '../../MembersTable';
import { useTranslation } from 'react-i18next';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { Button } from 'ncoded-component-library';
import SettingsIcon from 'icons/Settings.icon';
import ChatIcon from 'icons/Chat.icon';
import ManageBuyerGroup from '../../ManageBuyerGroup';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { DataRoomBuySideGroup } from 'router/subrouters/DataRoom/models/DataRoomMember';
import { getBuySideGroupAnchorId, getShowGroupChat } from 'utils';
import useGroupChatSellSideDiscovery from '../../../hooks/useGroupChatSellSideDiscovery';

import './BuySideInstance.styles.scss';

type BuySideInstanceProps = {
  className?: string;
  group: DataRoomBuySideGroup;
  showEditGroupButton: boolean;
};

const BuySideInstance: React.FC<BuySideInstanceProps> = ({
  className,
  group,
  showEditGroupButton = true,
}) => {
  const {
    name,
    activeChatSessionId,
    closedChatSessions = [],
    dataRoomMembers,
  } = group;

  const { doesntHaveAccess, openChatDrawer } = useContext(DataRoomContext);

  const { shouldHighlightGroupChatButton, markDrawerOpened } =
    useGroupChatSellSideDiscovery();

  const [buyGroup, buyGroupRef] = useCallbackRef<SideDrawerRef>(null);

  const showGroupChat = useMemo(
    () => getShowGroupChat(activeChatSessionId, dataRoomMembers),
    [dataRoomMembers, activeChatSessionId],
  );

  const previousGroupChats = useMemo(
    () => ({
      activeChatSessionId,
      closedChatSessions,
    }),
    [activeChatSessionId, closedChatSessions],
  );

  const classes = classNames('buy-side-instance', 'white-panel', className);

  const { t } = useTranslation();

  return (
    <div className={classes} id={getBuySideGroupAnchorId(group._id)}>
      <div className="instance-trigger">
        <div className="instance-trigger__title">
          <p className="name">{name}</p>
          <p className="num-of-members">
            {t('numOfMembers', { count: group.dataRoomMemberIds.length })}
          </p>

          {showGroupChat && (
            <Button
              variant="icon"
              icon={ChatIcon}
              className={classNames({
                'new-feature': shouldHighlightGroupChatButton,
              })}
              onClick={() => {
                markDrawerOpened();
                openChatDrawer(
                  activeChatSessionId,
                  activeChatSessionId,
                  previousGroupChats,
                );
              }}
            />
          )}

          {!doesntHaveAccess && showEditGroupButton && (
            <Button
              variant="icon"
              icon={SettingsIcon}
              onClick={() => buyGroup?.setOpen(true)}
            />
          )}
        </div>
      </div>

      <MembersTable isBuySide members={dataRoomMembers} />

      <ManageBuyerGroup ref={buyGroupRef} group={group} />
    </div>
  );
};

export default BuySideInstance;
